import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, Award, GitBranch, TrendingUp } from "lucide-react";
import AppShell, { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const INDUSTRY_LABELS = {
  hospitals: "Hospitals", pharma: "Pharma", diagnostics: "Diagnostics",
  medtech: "MedTech", biotech: "Biotech", digital_health: "Digital Health",
  insurance: "Insurance", health_ai: "Health AI", telemedicine: "Telemedicine",
  wellness: "Wellness", consulting: "Consulting",
};

const MOVE_META = {
  new_role: { label: "New role", icon: Briefcase },
  promotion: { label: "Promotion", icon: TrendingUp },
  board_appointment: { label: "Board appointment", icon: Award },
  transition: { label: "Transition", icon: GitBranch },
};

export default function LeaderProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [leader, setLeader] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/network/leaders/${id}`);
      setLeader(data);
    })();
  }, [id]);

  if (!leader) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  const label = INDUSTRY_LABELS[leader.industry] || leader.industry;
  const timeline = buildTimeline(leader);
  const isAuthed = !!user && user !== false;

  const content = (
    <div className={isAuthed ? "grid grid-cols-1 lg:grid-cols-3 gap-8" : "max-w-5xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8"}>
        {/* Left column: profile */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6 md:p-8">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-full bg-primary/8 text-primary flex items-center justify-center font-serif text-2xl shrink-0" aria-hidden>
                {initials(leader.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="overline">{label} · {leader.years_experience}+ years</div>
                <h1 className="mt-1 font-serif text-3xl md:text-4xl text-primary" data-testid="leader-name">{leader.name}</h1>
                <div className="mt-2 text-sm">
                  <span className="text-primary font-medium">{leader.current_title}</span>
                  <span className="text-muted-foreground"> at </span>
                  <span className="text-primary font-medium">{leader.current_organization}</span>
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><MapPin size={11} /> {leader.city}</span>
                  {leader.status === "in_transition" && (
                    <span className="rounded-full bg-accent/15 text-accent text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">In transition</span>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">{leader.bio}</p>
          </div>

          {/* Career timeline */}
          <div className="rounded-lg border border-border bg-white p-6 md:p-8">
            <div className="overline mb-4">Career timeline</div>
            <ol className="relative border-l-2 border-border ml-2 space-y-6" data-testid="leader-timeline">
              {timeline.map((item, idx) => (
                <li key={idx} className="pl-5">
                  <span className={`absolute -left-[9px] mt-1 w-4 h-4 rounded-full border-2 border-background ${item.current ? "bg-primary" : "bg-muted-foreground"}`} />
                  <div className="text-xs text-muted-foreground">{item.range}</div>
                  <div className="text-sm font-medium text-primary mt-0.5">{item.title}</div>
                  <div className="text-xs text-muted-foreground">{item.organization}{item.industry ? ` · ${INDUSTRY_LABELS[item.industry] || item.industry}` : ""}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Right column: moves */}
        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-primary text-primary-foreground p-6" data-testid="leader-sidecard">
            <div className="overline text-primary-foreground/70">Current role</div>
            <div className="mt-2 font-serif text-2xl leading-tight">{leader.current_title}</div>
            <div className="mt-1 text-sm text-primary-foreground/85">{leader.current_organization}</div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <div className="overline mb-3">Recent moves</div>
            {(!leader.moves || leader.moves.length === 0) ? (
              <div className="text-xs text-muted-foreground">No career moves recorded.</div>
            ) : (
              <ul className="space-y-4" data-testid="leader-moves-list">
                {leader.moves.map((m) => {
                  const Meta = MOVE_META[m.move_type] || MOVE_META.new_role;
                  const Icon = Meta.icon;
                  return (
                    <li key={m.id} className="text-sm border-l-2 border-border pl-3">
                      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Icon size={11} /> {Meta.label}
                      </div>
                      <div className="text-primary font-medium mt-0.5">{m.to_title}</div>
                      <div className="text-xs text-muted-foreground">{m.to_organization}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
  );

  if (isAuthed) {
    return (
      <AppShell
        pageTitle={leader.name}
        pageDescription={`${leader.current_title} at ${leader.current_organization}`}
        actions={<Link to="/network" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="back-to-network"><ArrowLeft size={14} /> Back to Network</Link>}
      >
        {content}
      </AppShell>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/network" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to Network</Link>
        </div>
      </header>
      {content}
      <Footer />
    </div>
  );
}

function buildTimeline(leader) {
  const items = [];
  items.push({
    range: `${leader.past_roles?.[0]?.end_year || guessStart(leader)} — Present`,
    title: leader.current_title,
    organization: leader.current_organization,
    industry: leader.industry,
    current: true,
  });
  (leader.past_roles || []).forEach((r) => {
    items.push({
      range: `${r.start_year} — ${r.end_year || ""}`,
      title: r.title,
      organization: r.organization,
      industry: r.industry,
      current: false,
    });
  });
  return items;
}

function guessStart(leader) {
  const now = new Date().getFullYear();
  return now - Math.min(leader.years_experience || 5, 3);
}

function initials(name) {
  if (!name) return "?";
  return name.replace(/^Dr\.?\s+/i, "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}
